import type { AppContext } from '@application/context/AppContext';
import { UpdateCoordinator } from '@application/update/UpdateCoordinator';
import type { UpdateManifest } from '@infra/update/UpdateService';
import { NetworkStatusService } from '@infra/network/NetworkStatusService';
import { ToastService } from '@presentation/components/Toast';
import { logger } from '@infra/telemetry/Logger';
import type { Session } from '@shared/types/User';

const PENDING_KEY = 'nexxus_pending_update_version';
const LAST_CHECK_KEY = 'nexxus_auto_update_last_check';
/** Vérification au plus toutes les 4 h (en plus du check au login). */
const CHECK_INTERVAL_MS = 4 * 3600 * 1000;

export interface AutoUpdateSnapshot {
  online: boolean;
  outdated: boolean;
  currentVersion: string;
  latestVersion: string | null;
  message: string;
  canInstall: boolean;
}

/**
 * Surveillance des mises à jour : check au login / retour réseau,
 * bandeau visible, téléchargement manuel un clic (sauvegarde obligatoire conservée).
 */
export class AutoUpdateMonitor {
  private coordinator: UpdateCoordinator;
  private pending: UpdateManifest | null = null;
  private timer: number | null = null;
  private bannerEl: HTMLElement | null = null;

  constructor(
    ctx: AppContext,
    private readonly currentVersion: string,
    private readonly host: HTMLElement,
    sessionHint?: () => Session | null,
  ) {
    this.coordinator = new UpdateCoordinator(ctx, currentVersion, sessionHint);
  }

  start(): void {
    void this.runCheck({ force: true, reason: 'login' });
    this.timer = window.setInterval(() => {
      void this.runCheck({ force: false, reason: 'interval' });
    }, CHECK_INTERVAL_MS);
    NetworkStatusService.subscribe((online) => {
      if (online) void this.runCheck({ force: true, reason: 'online' });
      else this.renderBanner(this.offlineSnapshot());
    });
  }

  stop(): void {
    if (this.timer != null) window.clearInterval(this.timer);
    this.timer = null;
    this.bannerEl?.remove();
    this.bannerEl = null;
  }

  getPending(): UpdateManifest | null {
    return this.pending;
  }

  async installPending(): Promise<boolean> {
    if (!NetworkStatusService.isBrowserOnline()) {
      ToastService.warning(
        'Hors ligne — connectez-vous à Internet pour installer la mise à jour (vos données restent locales).',
      );
      return false;
    }
    if (!this.pending?.is_outdated) {
      ToastService.info('Aucune mise à jour en attente');
      return false;
    }
    const ok = await this.coordinator.applyOnline(this.pending);
    if (ok) {
      localStorage.removeItem(PENDING_KEY);
      ToastService.success('Installation lancée — Nexxus ERP redémarrera après la mise à jour');
    }
    return ok;
  }

  private offlineSnapshot(): AutoUpdateSnapshot {
    const pendingVersion = localStorage.getItem(PENDING_KEY);
    return {
      online: false,
      outdated: Boolean(pendingVersion),
      currentVersion: this.currentVersion,
      latestVersion: pendingVersion,
      message: pendingVersion
        ? `Une mise à jour ${pendingVersion} est disponible — connectez-vous pour l’installer. Vos données restent dans l’application.`
        : 'Mode hors ligne — travail local actif. Les mises à jour seront proposées à la prochaine connexion.',
      canInstall: false,
    };
  }

  private async runCheck(opts: { force: boolean; reason: string }): Promise<void> {
    if (!NetworkStatusService.isBrowserOnline()) {
      this.renderBanner(this.offlineSnapshot());
      return;
    }

    if (!opts.force) {
      const last = localStorage.getItem(LAST_CHECK_KEY);
      if (last && Date.now() - new Date(last).getTime() < CHECK_INTERVAL_MS) return;
    }

    try {
      const manifest = await this.coordinator.checkOnline();
      localStorage.setItem(LAST_CHECK_KEY, new Date().toISOString());
      if (manifest?.is_outdated) {
        this.pending = manifest;
        localStorage.setItem(PENDING_KEY, manifest.latest_version);
        this.renderBanner({
          online: true,
          outdated: true,
          currentVersion: this.currentVersion,
          latestVersion: manifest.latest_version,
          message: `Mise à jour ${manifest.latest_version} disponible (actuelle ${this.currentVersion}). Vos données restent dans le dossier utilisateur — un clic pour installer après sauvegarde.`,
          canInstall: true,
        });
        if (opts.reason === 'login' || opts.reason === 'online') {
          ToastService.info(`Mise à jour ${manifest.latest_version} disponible`);
        }
        logger.info({
          event: 'auto_update_available',
          latest: manifest.latest_version,
          reason: opts.reason,
        });
      } else {
        this.pending = null;
        localStorage.removeItem(PENDING_KEY);
        this.bannerEl?.remove();
        this.bannerEl = null;
      }
    } catch (err) {
      logger.warn({
        event: 'auto_update_check_failed',
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  private renderBanner(snap: AutoUpdateSnapshot): void {
    const main = this.host.querySelector('main.main');
    if (!main) return;

    if (!snap.outdated && snap.online) {
      this.bannerEl?.remove();
      this.bannerEl = null;
      return;
    }

    let banner = this.bannerEl;
    if (!banner) {
      banner = document.createElement('div');
      banner.id = 'nexxus-update-banner';
      banner.setAttribute('role', 'status');
      main.prepend(banner);
      this.bannerEl = banner;
    }

    banner.style.cssText =
      'margin:12px 16px 0;padding:12px 16px;border-radius:8px;background:#0a121e;color:#fff;font-size:13px;display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;border:1px solid #008080;';

    const text = document.createElement('div');
    text.style.flex = '1';
    text.style.minWidth = '200px';
    text.textContent = snap.message;

    banner.replaceChildren(text);

    if (snap.canInstall) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'btn btn-sm';
      btn.style.cssText =
        'background:#008080;color:#fff;border:none;border-radius:6px;padding:8px 14px;cursor:pointer;font-weight:600;';
      btn.textContent = 'Installer maintenant';
      btn.addEventListener('click', () => {
        void this.installPending();
      });
      banner.appendChild(btn);
    }

    const link = document.createElement('a');
    link.href = 'https://nexxus.ga/telechargement-client';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.style.cssText = 'color:#80d8c8;font-size:12px;text-decoration:underline;';
    link.textContent = 'Télécharger l’installateur (secours)';
    link.addEventListener('click', (e) => {
      e.preventDefault();
      void window.electron?.openExternal?.(link.href);
    });
    banner.appendChild(link);
  }
}
